// decodeView() — RFC-102 P3 + P4 + P7.
// Parses a URL hash fragment back into a ViewerState. Tolerant: malformed
// payloads, unknown schema versions, and dropped layer IDs all return null
// or a partial DecodedView with diagnostic fields, never throw.
//
// The host uses null to mean "render the default catalog view" + telemetry
// event 'urlstate.unknown_version' / 'urlstate.malformed'.

import type { EngineId } from '@geosoft/viewer-sdk';
import { decodeUrlBase64ToUtf8 } from './base64';
import {
  HASH_PREFIX_EDITABLE,
  HASH_PREFIX_READONLY,
  URL_STATE_SCHEMA_VERSION,
  type CompactScenario,
  type DecodedView,
  type Scenario,
  type ScenarioOverrides,
  type UrlViewState,
  type ViewerState,
  type ViewTime,
} from './schema';

const VALID_ENGINES: ReadonlyArray<EngineId> = [
  'maplibre',
  'deckgl',
  'cesium',
  'three',
  'giro3d',
];

function isValidEngine(value: unknown): value is EngineId {
  return typeof value === 'string' && (VALID_ENGINES as ReadonlyArray<string>).includes(value);
}

function parseTime(value: unknown): ViewTime | undefined {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value !== 'string') return undefined;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return undefined;
  return d;
}

function parseScenario(c: unknown): Scenario | null {
  if (typeof c !== 'object' || c === null) return null;
  const o = c as Partial<CompactScenario>;
  if (typeof o.id !== 'string' || typeof o.lab !== 'string' || !Array.isArray(o.lids)) return null;
  const lids: string[] = [];
  for (const x of o.lids) {
    if (typeof x !== 'string') continue;
    lids.push(x);
  }
  const s: Scenario = {
    id: o.id,
    label: o.lab,
    layerIds: lids,
  };
  const pt = parseTime(o.pt);
  if (pt !== undefined) s.pinnedTime = pt;
  if (o.lov && typeof o.lov === 'object') {
    const overrides: Record<string, ScenarioOverrides> = {};
    for (const [layerId, raw] of Object.entries(o.lov)) {
      if (typeof raw !== 'object' || raw === null) continue;
      const r = raw as { st?: unknown; vis?: { mn?: unknown; mx?: unknown } };
      const entry: ScenarioOverrides = {};
      if (r.st !== undefined && typeof r.st === 'object' && r.st !== null) {
        entry.style = r.st as Record<string, unknown>;
      }
      if (r.vis && typeof r.vis === 'object') {
        const vis: { minZoom?: number; maxZoom?: number } = {};
        if (typeof r.vis.mn === 'number') vis.minZoom = r.vis.mn;
        if (typeof r.vis.mx === 'number') vis.maxZoom = r.vis.mx;
        if (Object.keys(vis).length > 0) entry.visibility = vis;
      }
      if (Object.keys(entry).length > 0) overrides[layerId] = entry;
    }
    if (Object.keys(overrides).length > 0) s.layerOverrides = overrides;
  }
  if (typeof o.n === 'string') s.notes = o.n;
  return s;
}

/**
 * Convert a parsed UrlViewState back into the in-app ViewerState. Pure
 * function; useful for tests.
 */
export function fromUrlViewState(u: UrlViewState): ViewerState {
  const state: ViewerState = {};
  const view: NonNullable<ViewerState['view']> = {};
  if (Array.isArray(u.c) && u.c.length === 2 && typeof u.c[0] === 'number' && typeof u.c[1] === 'number') {
    view.center = [u.c[0], u.c[1]];
  }
  if (typeof u.z === 'number') view.zoom = u.z;
  if (typeof u.h === 'number') view.heading = u.h;
  if (typeof u.p === 'number') view.pitch = u.p;
  if (Object.keys(view).length > 0) state.view = view;

  const t = parseTime(u.t);
  if (t !== undefined) state.time = t;

  if (Array.isArray(u.l)) {
    const ids: string[] = [];
    for (const x of u.l) if (typeof x === 'string') ids.push(x);
    state.visibleLayerIds = ids;
  }
  if (isValidEngine(u.e)) state.primaryEngine = u.e;

  if (Array.isArray(u.sc)) {
    const scenarios: Scenario[] = [];
    for (const raw of u.sc) {
      const s = parseScenario(raw);
      if (s) scenarios.push(s);
    }
    state.scenarios = scenarios;
  }
  if (u.sa !== undefined) state.activeScenarioId = u.sa ?? null;
  if (u.cmp !== undefined) state.compareWithScenarioId = u.cmp ?? null;
  if (typeof u.o === 'string') state.orgAlias = u.o;
  if (u.fl && typeof u.fl === 'object') {
    const fl: Record<string, boolean> = {};
    for (const [k, v] of Object.entries(u.fl)) {
      if (typeof v === 'boolean') fl[k] = v;
    }
    if (Object.keys(fl).length > 0) state.featureFlags = fl;
  }
  return state;
}

export interface DecodeFailure {
  reason: 'no-hash' | 'unknown-prefix' | 'malformed-base64' | 'malformed-json' | 'unknown-version';
  /** Schema version observed (when relevant). */
  observedVersion?: number;
}

export type DecodeResult = DecodedView | DecodeFailure;

function isFailure(r: DecodeResult): r is DecodeFailure {
  return (r as DecodeFailure).reason !== undefined;
}

export { isFailure };

export function decodeView(hash: string | null | undefined): DecodeResult {
  if (typeof hash !== 'string' || hash.length === 0) {
    return { reason: 'no-hash' };
  }

  let readOnly: boolean;
  let payload: string;
  if (hash.startsWith(HASH_PREFIX_READONLY)) {
    readOnly = true;
    payload = hash.slice(HASH_PREFIX_READONLY.length);
  } else if (hash.startsWith(HASH_PREFIX_EDITABLE)) {
    readOnly = false;
    payload = hash.slice(HASH_PREFIX_EDITABLE.length);
  } else {
    return { reason: 'unknown-prefix' };
  }

  const json = decodeUrlBase64ToUtf8(payload);
  if (json === null) return { reason: 'malformed-base64' };

  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return { reason: 'malformed-json' };
  }

  if (typeof parsed !== 'object' || parsed === null) {
    return { reason: 'malformed-json' };
  }
  const u = parsed as Partial<UrlViewState>;
  if (u.v !== URL_STATE_SCHEMA_VERSION) {
    return { reason: 'unknown-version', observedVersion: typeof u.v === 'number' ? u.v : undefined };
  }

  const state = fromUrlViewState(u as UrlViewState);
  return { state, readOnly };
}
